import { DateHelper, DateValue, NullableDateValue } from '@/util/DateHelper';
import { IRideitNoticeTargetCreateRequest, RideitNoticeTarget } from './RideitNoticeTarget';

export enum NoticePriority {
  Info,
  Warning,
  Danger,
  Sticky
}

export enum NoticeStatus {
  Draft,
  Active,
  Deleted
}

export enum DisplayedNoticeStatus {
  Draft,
  Active,
  Inactive,
  Deleted,
}

export interface IRideitNotice {
  id: string;
  title: string;
  text: string;
  externalLink: string;
  validFrom: NullableDateValue;
  validUntil: NullableDateValue;
  priority: NoticePriority;
  status: NoticeStatus;
  createdAt: DateValue;
  createdBy: string;
  updatedAt?: NullableDateValue;
  updatedBy?: string | null;
  targets: RideitNoticeTarget[];
}


export class RideitNotice {
  private readonly dateHelper: DateHelper = new DateHelper();
  
  id: string;
  title: string;
  text: string;
  externalLink: string;
  validFrom: NullableDateValue;
  validUntil: NullableDateValue;
  priority: NoticePriority;
  status: NoticeStatus;
  createdAt: DateValue;
  createdBy: string;
  updatedAt?: NullableDateValue;
  updatedBy?: string | null;
  targets: RideitNoticeTarget[];


  constructor(data?: IRideitNotice | null) {
    this.id = data?.id ?? '';
    this.title = data?.title ?? '';
    this.text = data?.text ?? '';
    this.externalLink = data?.externalLink ?? '';
    this.validFrom = data?.validFrom ?? null;
    this.validUntil = data?.validUntil ?? null;
    this.priority = data?.priority ?? NoticePriority.Info;
    this.status = data?.status ?? NoticeStatus.Active;
    this.createdAt = data?.createdAt ?? '';
    this.createdBy = data?.createdBy ?? '';
    this.updatedAt = data?.updatedAt ?? null;
    this.updatedBy = data?.updatedBy ?? null;
    this.targets = data?.targets?.map(t => new RideitNoticeTarget(t)) ?? [];
  }

  get displayedStatus(): DisplayedNoticeStatus {
    switch (this.status) {
      case NoticeStatus.Draft:
        return DisplayedNoticeStatus.Draft;
      case NoticeStatus.Active:
        return this.getActiveStatus();
      case NoticeStatus.Deleted:
        return DisplayedNoticeStatus.Deleted;
      default:
        return DisplayedNoticeStatus.Draft;
    }
  }

  toCreateRequest(): IRideitNoticeCreateRequest {
    return {
      title: this.title,
      text: this.text,
      externalLink: this.externalLink,
      validFrom: this.validFrom,
      validUntil: this.validUntil,
      priority: this.priority,
      status: this.status,
      targets: this.targets?.map(t => t.toCreateRequest()) ?? [],
    };
  }

  private getActiveStatus(): DisplayedNoticeStatus {
    const isActive = this.dateHelper.isInValidityPeriod(this.validFrom, this.validUntil);
    if (!isActive) {
      return DisplayedNoticeStatus.Inactive;
    }
    return DisplayedNoticeStatus.Active;
  }
}

export interface IRideitNoticeCreateRequest {
  text: string;
  title: string;
  externalLink: string;
  validFrom: NullableDateValue;
  validUntil: NullableDateValue;
  priority: NoticePriority;
  status: NoticeStatus;
  targets: IRideitNoticeTargetCreateRequest[];
}

